/*
 * h4h-megamenu-a11y.js
 *
 * OM52 §5 — two defects found while testing the eight-tile mega-menu with the
 * keyboard rather than the mouse. Both predate the tiles; the tiles are what
 * made anyone open the menus with Tab.
 *
 * 1. THE PANEL CANNOT BE OPENED BY KEYBOARD.
 *    Measured: focus the `<summary>` of "Shop by product type", press Enter,
 *    and the browser NAVIGATES to /collections/all. `details.open` stays
 *    `false`, `aria-expanded` stays `"false"`, and zero tiles become visible.
 *    Ella opens the panel on HOVER, and a keyboard has no hover, so every
 *    child link behind these three menus was unreachable without a mouse.
 *
 * 2. THE THREE TOP-LEVEL DESTINATIONS ARE NOT LINKS.
 *    They are `<span class="header__menu-parent-link" data-href="…">` with a
 *    JS click handler — no `href`, not focusable, and announced as plain text
 *    rather than as a link. Measured: `header a[href="/collections/all"]`
 *    matches nothing anywhere in the header.
 *
 * THE FIX, in the order a keyboard user meets it:
 *   * Enter and Space on the summary TOGGLE the panel, which is what a
 *     `<summary>` is for. Mouse behaviour is untouched — a click on the label
 *     still follows `data-href`, because that is the merchant's configured
 *     intent and changing it would change the menu for everyone.
 *   * The parent destination is no longer lost: it is inserted as a real
 *     `<a href>` at the TOP of the panel it belongs to, carrying the parent's
 *     own name. Nothing is invented — the href is the `data-href` that was
 *     already there — and it is inserted once, guarded by a marker class, so a
 *     re-render cannot duplicate it.
 *   * Escape closes the open panel and returns focus to its summary.
 *
 * Defensive throughout: if the markup is not what it expects, it does nothing.
 * A menu that behaves as it did today is a better failure than a broken one.
 */
(function () {
  'use strict';

  var MARK = 'h4h-parent-link';

  function panelOf(details) {
    return details.querySelector('.mega-menu__content, .header__submenu, .mega-menu__inner') ||
           details.querySelector('ul') ||
           details;
  }

  function ensureParentLink(details) {
    var span = details.querySelector('summary [data-href]');
    if (!span) return;
    var href = span.getAttribute('data-href');
    if (!href) return;
    if (details.querySelector('.' + MARK)) return;      // already inserted

    var panel = panelOf(details);
    if (!panel || panel === details) return;

    var li = document.createElement('li');
    li.className = MARK + '__item';
    var a = document.createElement('a');
    a.className = MARK;
    a.setAttribute('href', href);
    a.textContent = (span.textContent || '').trim();
    li.appendChild(a);

    if (panel.tagName === 'UL' || panel.tagName === 'OL') {
      panel.insertBefore(li, panel.firstChild);
    } else {
      var firstList = panel.querySelector('ul, ol');
      if (firstList) firstList.insertBefore(li, firstList.firstChild);
      else panel.insertBefore(a, panel.firstChild);
    }
  }

  function wire(details) {
    var summary = details.querySelector('summary');
    if (!summary || summary.dataset.h4hWired === '1') return;
    summary.dataset.h4hWired = '1';

    summary.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault();
      e.stopPropagation();

      if (details.open) {
        details.open = false;
        summary.setAttribute('aria-expanded', 'false');
        return;
      }

      /* The panel's contents are NOT in the document until Ella fetches them:
         the `<details is="mega-menu-details" activate-event="hover">` holds a
         `<section-fetcher>` that loads on hover. Setting `details.open = true`
         by itself therefore opened an EMPTY panel — measured, `links: 1` (only
         the link this script inserts) and zero tiles. So the vendor's own
         activation path is triggered rather than reimplemented: dispatch the
         pointer events it listens for, then confirm the open state. */
      ['pointerenter', 'mouseenter', 'pointerover', 'mouseover'].forEach(function (type) {
        var ev;
        try {
          ev = new PointerEvent(type, { bubbles: true, cancelable: true });
        } catch (err) {
          ev = new MouseEvent(type.replace('pointer', 'mouse'), { bubbles: true, cancelable: true });
        }
        summary.dispatchEvent(ev);
        details.dispatchEvent(ev);
      });

      var tries = 0;
      (function settle() {
        tries++;
        if (!details.open) details.open = true;
        summary.setAttribute('aria-expanded', 'true');
        ensureParentLink(details);
        var links = details.querySelectorAll('.mega-menu__content a[href]');
        if (links.length > 1 || tries > 12) {
          var first = links[0];
          if (first) {
            try { first.focus({ preventScroll: true }); } catch (err) { first.focus(); }
          }
          return;
        }
        setTimeout(settle, 60);   /* wait for section-fetcher to populate */
      })();
    }, true);

    details.addEventListener('toggle', function () {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      if (details.open) ensureParentLink(details);
    });
  }

  function init() {
    var menus = document.querySelectorAll('header details.mega-menu, .header details.mega-menu');
    for (var i = 0; i < menus.length; i++) wire(menus[i]);
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var open = document.querySelector('header details.mega-menu[open]');
    if (!open) return;
    open.open = false;
    var s = open.querySelector('summary');
    if (s) {
      s.setAttribute('aria-expanded', 'false');
      try { s.focus({ preventScroll: true }); } catch (err) { s.focus(); }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', init);
})();
